import { useTranslate } from "@tolgee/react";
import { Check, Copy } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "#/components/ui/button";
import { Input } from "#/components/ui/input";
import { useCreateReferralCode } from "#/hooks/use-create-referral-code";
import { analytics } from "#/lib/analytics";
import {
	classifyReferralError,
	type ReferralErrorKind,
} from "#/lib/api/referrals";
import { REFERRAL_MIN_USD } from "../lib/config";
import { buildInviteLink } from "../lib/share";
import { ReferralFeeDialog } from "./referral-fee-dialog";

const CODE_REGEX = /^[A-Z0-9_-]{3,15}$/;
const MAX_LEN = 15;

interface Props {
	existingCode: string | null;
}

export function ReferralCodeCard({ existingCode }: Props) {
	if (existingCode) {
		return <ExistingCode code={existingCode} />;
	}
	return <CreateCodeForm />;
}

function ExistingCode({ code }: { code: string }) {
	const { t } = useTranslate();
	const [copied, setCopied] = useState(false);
	const link = buildInviteLink(code);

	function handleCopy() {
		if (typeof navigator === "undefined") return;
		navigator.clipboard
			.writeText(link)
			.then(() => {
				setCopied(true);
				toast.success(t("referrals.code.link-copied"));
				analytics.track("referral_link_copied", {});
				window.setTimeout(() => setCopied(false), 1500);
			})
			.catch(() => {
				toast.error(t("referrals.code.error-generic"));
			});
	}

	return (
		<section>
			<h2 className="text-body2-medium text-muted-foreground">
				{t("referrals.code.your-code")}
			</h2>
			<p className="mt-2 text-display4 text-foreground">{code}</p>
			<div className="mt-2">
				<ReferralFeeDialog code={code} />
			</div>

			<div className="mt-5">
				<label className="text-label2-medium text-muted-foreground">
					{t("referrals.code.share-link")}
				</label>
				<div className="mt-1.5 flex items-center gap-2">
					<Input
						readOnly
						value={link}
						className="text-label2 md:text-label2"
					/>
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={handleCopy}
						aria-label={t("referrals.code.copy")}
					>
						{copied ? (
							<Check className="size-4" />
						) : (
							<Copy className="size-4" />
						)}
						<span>{t("referrals.code.copy")}</span>
					</Button>
				</div>
			</div>
		</section>
	);
}

function CreateCodeForm() {
	const { t } = useTranslate();
	const [value, setValue] = useState("");
	const [errorKey, setErrorKey] = useState<string | null>(null);
	const mutation = useCreateReferralCode();

	const normalized = value.trim();
	const isValid = CODE_REGEX.test(normalized);
	const hintIsError = normalized.length > 0 && !isValid;

	function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!isValid) {
			setErrorKey("referrals.code.hint");
			return;
		}
		setErrorKey(null);
		mutation.mutate(normalized, {
			onError: (err) => {
				const kind = classifyReferralError(err);
				const key = errorKeyFor(kind);
				setErrorKey(key);
				// already-set triggers /auth/me refetch in the hook — the UI will
				// flip to the read-only view; no toast needed.
				if (kind !== "already-set") {
					toast.error(t(key, { amount: String(REFERRAL_MIN_USD) }));
				}
			},
		});
	}

	const isBusy = mutation.isPending;

	return (
		<section>
			<h2 className="text-headline text-foreground">
				{t("referrals.code.create-title")}
			</h2>
			<p className="mt-1 text-body2 text-muted-foreground">
				{t("referrals.code.create-subtitle")}
			</p>
			<form
				onSubmit={handleSubmit}
				className="mt-4 flex flex-col gap-3 sm:flex-row"
			>
				<div className="flex-1">
					<Input
						value={value}
						onChange={(event) => {
							setValue(event.target.value.toUpperCase().slice(0, MAX_LEN));
							if (errorKey) setErrorKey(null);
						}}
						placeholder={t("referrals.code.placeholder")}
						disabled={isBusy}
						maxLength={MAX_LEN}
						autoComplete="off"
						aria-invalid={hintIsError || errorKey ? true : undefined}
					/>
					<div className="mt-1.5 flex items-center justify-between text-label2">
						<span
							className={
								errorKey
									? "text-destructive"
									: hintIsError
										? "text-destructive"
										: "text-muted-foreground"
							}
						>
							{errorKey
								? t(errorKey, { amount: String(REFERRAL_MIN_USD) })
								: t("referrals.code.hint")}
						</span>
						<span className="text-muted-foreground tabular-nums">
							{normalized.length}/{MAX_LEN}
						</span>
					</div>
				</div>
				<Button
					type="submit"
					disabled={isBusy || !isValid}
					className="self-start"
				>
					{isBusy ? t("referrals.code.submitting") : t("referrals.code.submit")}
				</Button>
			</form>
			<p className="mt-3 text-label2 text-muted-foreground">
				{t("referrals.code.permanent-warning", {
					amount: String(REFERRAL_MIN_USD),
				})}
			</p>
		</section>
	);
}

function errorKeyFor(kind: ReferralErrorKind): string {
	switch (kind) {
		case "invalid-format":
			return "referrals.code.hint";
		case "reserved":
			return "referrals.code.error-reserved";
		case "already-taken":
			return "referrals.code.error-taken";
		case "already-set":
			return "referrals.code.error-already-set";
		case "insufficient-balance":
			return "referrals.code.error-insufficient-balance";
		case "hyperliquid-unreachable":
			return "referrals.code.error-hyperliquid-unreachable";
		case "unauthorized":
			return "referrals.code.error-unauthorized";
		default:
			return "referrals.code.error-generic";
	}
}
